import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import { Trophy, Medal, Timer, MapPin, ArrowLeft, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getMaxLoopsRecords,
  getFastestRaceTimes,
  getAppearanceLeaders,
  listKUTCEditions,
  LoopRecord,
  FastestTimeRecord,
  AppearanceRecord
} from '../services/kutcResultsService';

import { getMilestoneRecords } from '../services/kutcMilestoneService';
import { MilestoneRecord } from '../utils/kutcMilestoneRecords';

const KUTCRecordsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [milestones, setMilestones] = useState<Map<number, MilestoneRecord[]>>(new Map());
  const [milestoneError, setMilestoneError] = useState(false);
  const [loopRecords, setLoopRecords] = useState<LoopRecord[]>([]);
  const [fastestTimes, setFastestTimes] = useState<Map<string, FastestTimeRecord[]>>(new Map());
  const [appearanceGroups, setAppearanceGroups] = useState<{ top: AppearanceRecord[]; runnerUp: AppearanceRecord[] }>({
    top: [],
    runnerUp: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasDataIntegrityIssues, setHasDataIntegrityIssues] = useState(false);

  useEffect(() => {
    const fetchRecords = async () => {
      try {
        setLoading(true);
        setMilestoneError(false);
        const editions = await listKUTCEditions();
        setHasDataIntegrityIssues(editions.some(edition => edition.metadata?.resultsStatus === 'error'));

        const [loops, times, appearances, passages] = await Promise.all([
          getMaxLoopsRecords(),
          getFastestRaceTimes(),
          getAppearanceLeaders(),
          getMilestoneRecords(editions).catch(err => {
            console.error('Error fetching milestone records:', err);
            setMilestoneError(true);
            return new Map<number, MilestoneRecord[]>();
          })
        ]);
        
        setMilestones(passages);
        setLoopRecords(loops);
        setFastestTimes(times);
        setAppearanceGroups(appearances);
      } catch (err) {
        console.error('Error fetching records:', err);
        setError(t('kutc.records.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchRecords();
  }, [t]);

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const getMedalIcon = (index: number) => {
    const colors = ['#FFD700', '#C0C0C0', '#CD7F32']; // gold, silver, bronze
    if (index >= 3) return null;
    return <Medal size={28} color={colors[index]} style={{ marginRight: 8 }} />;
  };

  const publishedDistances = ['4-loops', '8-loops', '12-loops', '16-loops', '20-loops', '24-loops'];
  const publishedFastestTimes = Array.from(fastestTimes.entries())
    .filter(([distanceKey, records]) => publishedDistances.includes(distanceKey) && records.length > 0)
    .sort(([distanceA], [distanceB]) => publishedDistances.indexOf(distanceA) - publishedDistances.indexOf(distanceB));

  const formatAppearances = (count: number) => t('kutc.records.appearancesCount', { count });
  const topGroup = appearanceGroups.top;
  const runnerUpGroup = appearanceGroups.runnerUp;
  const topAppearancesLabel = topGroup.length > 0 ? formatAppearances(topGroup[0].appearances) : null;
  const runnerUpAppearancesLabel = runnerUpGroup.length > 0 ? formatAppearances(runnerUpGroup[0].appearances) : null;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Navigation */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
        <Button
          startIcon={<ArrowLeft />}
          onClick={() => navigate('/kutc/results')}
        >
          {t('kutc.backToOverview')}
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        <Button
          variant="outlined"
          startIcon={<BarChart3 />}
          onClick={() => navigate('/kutc/all-time')}
        >
          {t('kutc.allTimeLeaderboard')}
        </Button>
      </Box>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
          <Trophy size={40} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          {t('kutc.records.title')}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {t('kutc.records.subtitle')}
        </Typography>
      </Box>

      {hasDataIntegrityIssues && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('kutc.dataIntegrityWarning')}
        </Alert>
      )}

      {/* Max Loops Section */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" component="h2" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
          <Trophy size={32} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          {t('kutc.records.mostLoopsCompleted')}
        </Typography>

        {loopRecords.length > 0 ? (
          <Paper elevation={2} sx={{ p: 3 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>{t('kutc.records.table.rank')}</strong></TableCell>
                    <TableCell><strong>{t('kutc.records.table.participant')}</strong></TableCell>
                    <TableCell align="center"><strong>{t('kutc.records.table.loops')}</strong></TableCell>
                    <TableCell align="center"><strong>{t('kutc.records.table.time')}</strong></TableCell>
                    <TableCell align="center"><strong>{t('kutc.records.table.edition')}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loopRecords.map((record, index) => (
                    <TableRow key={`${record.personId}-${record.editionId}`}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getMedalIcon(index)}
                          <Typography variant="body2">{index + 1}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {record.firstName} {record.lastName}
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={record.loopsCompleted} color="primary" size="small" />
                      </TableCell>
                      <TableCell align="center">{record.totalTimeDisplay}</TableCell>
                      <TableCell align="center">{record.year}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : (
          <Alert severity="info">{t('kutc.records.noLoopRecords')}</Alert>
        )}
      </Box>

      {/* Fastest Times Section */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" component="h2" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
          <Timer size={32} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          {t('kutc.records.fastestRaceTimesTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
          {t('kutc.records.fastestRaceTimesIntro')}
        </Typography>

        {publishedFastestTimes.map(([distanceKey, records]) => (
          <Paper key={distanceKey} elevation={2} sx={{ mb: 3, p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {records[0]?.raceName || distanceKey}
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>{t('kutc.records.table.rank')}</strong></TableCell>
                    <TableCell><strong>{t('kutc.records.table.participant')}</strong></TableCell>
                    <TableCell align="center"><strong>{t('kutc.records.table.time')}</strong></TableCell>
                    <TableCell align="center"><strong>{t('kutc.records.table.edition')}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {records.slice(0, 3).map((record, index) => (
                    <TableRow key={`${record.personId}-${record.editionId}`}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getMedalIcon(index)}
                          <Typography variant="body2">{index + 1}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {record.firstName} {record.lastName}
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={record.timeDisplay} color="primary" size="small" />
                      </TableCell>
                      <TableCell align="center">{record.year}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ))}

        {publishedFastestTimes.length === 0 && (
          <Alert severity="info">{t('kutc.records.noRaceTimeRecords')}</Alert>
        )}
      </Box>

      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" component="h2" gutterBottom fontWeight="bold">
          {t('kutc.records.milestonesTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 900 }}>
          {t('kutc.records.milestonesIntro')}
        </Typography>
        <Alert severity="info" sx={{ mb: 3 }}>{t('kutc.records.milestonesCoverage')}</Alert>
        {milestoneError ? <Alert severity="warning">{t('kutc.records.milestonesFailed')}</Alert> :
          Array.from(milestones).map(([loops, records]) => (
            <Paper key={loops} elevation={2} sx={{ mb: 3, p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                {t('kutc.records.milestoneLoops', { count: loops })}
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead><TableRow>
                    {['rank', 'participant', 'time', 'edition', 'enteredRace', 'completedLoops', 'timingSource'].map(key =>
                      <TableCell key={key}><strong>{t(`kutc.records.table.${key}`)}</strong></TableCell>)}
                  </TableRow></TableHead>
                  <TableBody>{records.map(record => (
                    <TableRow key={`${record.editionId}-${record.personId}`}>
                      <TableCell>{record.rank}</TableCell>
                      <TableCell>{record.firstName} {record.lastName}</TableCell>
                      <TableCell><Chip label={record.timeDisplay} color="primary" size="small" /></TableCell>
                      <TableCell>{record.year}</TableCell>
                      <TableCell>{record.enteredRace || '–'}</TableCell>
                      <TableCell>{record.completedLoops}</TableCell>
                      <TableCell>{t(`kutc.records.source.${record.source}`)}</TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ))}
        {!milestoneError && milestones.size === 0 &&
          <Alert severity="info">{t('kutc.records.noRaceTimeRecords')}</Alert>}
      </Box>

      {/* Most Appearances Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
          <MapPin size={32} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          {t('kutc.records.mostAppearancesTitle')}
        </Typography>

        <Paper elevation={2} sx={{ p: 3 }}>
          {topGroup.length > 0 ? (
            <>
              {topAppearancesLabel && (
                <Typography variant="h5" color="primary" gutterBottom>
                  {topAppearancesLabel}
                </Typography>
              )}
              
              <Grid container spacing={2}>
                {topGroup.map((leader) => (
                  <Grid item xs={12} sm={6} md={4} key={leader.personId}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                          {leader.firstName} {leader.lastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('kutc.records.participatedIn', { editions: leader.editions.join(', ').replace(/kutc-/g, '') })}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {runnerUpGroup.length > 0 && runnerUpAppearancesLabel && (
                <>
                  <Typography variant="h6" fontWeight="bold" sx={{ mt: 4 }}>
                    {t('kutc.records.runnersUp', { label: runnerUpAppearancesLabel })}
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {runnerUpGroup.map((leader) => (
                      <Grid item xs={12} sm={6} md={4} key={`runner-${leader.personId}`}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                              {leader.firstName} {leader.lastName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t('kutc.records.participatedIn', { editions: leader.editions.join(', ').replace(/kutc-/g, '') })}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}
            </>
          ) : (
            <Alert severity="info">{t('kutc.records.noAppearanceRecords')}</Alert>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default KUTCRecordsPage;
